import { includes } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { TeamInsuranceProduct } from 'types';
import { FilterType, UseProductFilterAttributes } from './types';
import { useCoverageLineFilter } from './useCoverageLineFilter';
import { useIsAdmittedFilter } from './useIsAdmittedFilter';
import { useIsPrimaryFilter } from './useIsPrimaryFilter';

export function useProductFilters(
  teamProducts: TeamInsuranceProduct[],
  isLoading: boolean,
  paginationReset: () => void,
  includeExtraFilters: boolean,
): UseProductFilterAttributes {
  const { coverageLineFilter, preSelectedCoverageLines } = useCoverageLineFilter({
    paginationReset,
  });

  const isAdmittedFilter = useIsAdmittedFilter({
    teamProducts,
    teamProductsLoaded: !isLoading,
    paginationReset,
  });

  const isPrimaryFilter = useIsPrimaryFilter({
    teamProducts,
    teamProductsLoaded: !isLoading,
    paginationReset,
  });

  const [searchInput, setSearchInput] = useState<string>('');

  // Filter out chips that are not part of the pre-selected coverage lines
  const initialCoverageLineFilters = useMemo(
    () => coverageLineFilter.filters.filter((filter) => includes(preSelectedCoverageLines, filter.key)),
    [coverageLineFilter.filters, preSelectedCoverageLines],
  );

  // Function to toggle chip selection state (checked/unchecked)
  const handleVisibleCoverageChange = useCallback(
    (key: string) => {
      coverageLineFilter.onFilterToggle(key);
    },
    [coverageLineFilter],
  );

  const isPrimaryProduct = (teamProduct: TeamInsuranceProduct) =>
    teamProduct.insuranceProduct.coverageLayer?.isPrimary || !teamProduct.insuranceProduct.coverageLayer?.isExcess;

  const filteredProducts = useMemo(
    () =>
      [...teamProducts]
        .sort((a, b) => {
          if (a.insuranceProduct.marketName !== b.insuranceProduct.marketName) {
            return a.insuranceProduct.marketName.localeCompare(b.insuranceProduct.marketName);
          }
          if (isPrimaryProduct(a) !== isPrimaryProduct(b)) {
            return isPrimaryProduct(a) ? -1 : 1;
          }
          return a.insuranceProduct.isAdmitted ? -1 : 1;
        })
        .filter((teamProduct) => {
          if (coverageLineFilter.isAllSelected) {
            return true; // No filters applied
          }
          return coverageLineFilter.filters.some(
            (filter) =>
              filter.checked &&
              teamProduct.insuranceProduct.coverageLines.some((line) => line.coverageLine === filter.key),
          );
        })
        .filter((teamProduct) => {
          if (isAdmittedFilter.isAllSelected || !includeExtraFilters) {
            return true;
          }
          if (isAdmittedFilter.filters.every((filter) => filter.checked)) {
            return true;
          }
          if (isAdmittedFilter.filters[0].checked) {
            return teamProduct.insuranceProduct.isAdmitted;
          }
          if (isAdmittedFilter.filters[1].checked) {
            return !teamProduct.insuranceProduct.isAdmitted;
          }
          return true;
        })
        .filter((teamProduct) => {
          if (isPrimaryFilter.isAllSelected || !includeExtraFilters) {
            return true;
          }
          if (isPrimaryFilter.filters.every((filter) => filter.checked)) {
            return true;
          }
          if (isPrimaryFilter.filters[1].checked) {
            return teamProduct.insuranceProduct.coverageLayer?.isExcess;
          }
          if (isPrimaryFilter.filters[0].checked) {
            return isPrimaryProduct(teamProduct);
          }
          return true;
        })
        .filter((teamProduct) => {
          if (searchInput.length < 2) {
            return true;
          }
          const productTerms = [teamProduct.insuranceProduct.marketName].filter(Boolean);
          return productTerms.some((term) => term?.toLowerCase().includes(searchInput.toLowerCase()));
        }),
    [
      teamProducts,
      coverageLineFilter.isAllSelected,
      coverageLineFilter.filters,
      isAdmittedFilter.isAllSelected,
      isAdmittedFilter.filters,
      includeExtraFilters,
      isPrimaryFilter.isAllSelected,
      isPrimaryFilter.filters,
      searchInput,
    ],
  );

  const setSearch = useCallback(
    (value: string) => {
      paginationReset();
      setSearchInput(value);
    },
    [paginationReset],
  );

  const clearFilters = useCallback(() => {
    coverageLineFilter.handleSelectAll();
    isAdmittedFilter.handleSelectAll();
    isPrimaryFilter.handleSelectAll();
  }, [coverageLineFilter, isAdmittedFilter, isPrimaryFilter]);

  const isFilterApplied =
    !coverageLineFilter.isAllSelected || !isAdmittedFilter.isAllSelected || !isPrimaryFilter.isAllSelected;

  const filters = {
    [FilterType.CoverageLine]: coverageLineFilter,
    [FilterType.IsAdmitted]: isAdmittedFilter,
    [FilterType.IsPrimary]: isPrimaryFilter,
  };

  return {
    sortedAndFilteredItems: filteredProducts,
    filters,
    clearFilters,
    isFilterApplied,
    search: searchInput,
    setSearch,
    coverageLineFilters: initialCoverageLineFilters, // Chips filtered by pre-selected coverage lines
    handleVisibleCoverageChange, // Toggle chip selection
  };
}
